<template>
  <div class="container">
    <donut-page-title title="Permissions" />
    <donut-form-item
      class="m-tx4"
      label="Shoutouts & Points Admins"
      :description="'Shoutouts & Points Admins can manage all Points, Shoutouts ' +
        'and reward settings, including setting up automated gift card ' +
        'rewards that are billed to your account.'"
    >
      <div class="f-v">
        <div v-for="admin in admins" :key="admin.id">
          <div class="f-sb f-ac h-x5">
            <div class="t">{{ admin.name }}</div>
            <div v-if="permissionsLabel(admin)" class="t gray">{{ permissionsLabel(admin) }}</div>
            <button v-else :class="removeButtonClasses(admin)" @click="removeAdmin(admin)">
              <i class="mi">close</i>
            </button>
          </div>
          <div class="separator m-vx1" />
        </div>
        <donut-selector-prefabs-user v-model="userInSelector" :class="{ disabled: userInSelector }" />
      </div>
    </donut-form-item>
  </div>
</template>

<script setup>
 import Network from 'lib/api/network';
 import { ref, reactive, computed, inject, watch, nextTick, onBeforeMount } from 'vue';
 import { useStore } from 'vuex';
 import { capitalize } from 'lodash';

 const { state, commit, dispatch } = useStore();

 const props = defineProps({
   pointsConfig: { type: Object, required: true },
 });
 const $i18n = inject('$i18n');

 const userInSelector = ref(null);

 // Admin List
 const admins = reactive(props.pointsConfig.permission_users);
 const userCurrentlyProcessing = reactive({});
 const showRemoveButton = (admin) => !admin.isCurrentUser;
 const removeButtonClasses = (admin) => {
   let classList = 'inv h-x4 w-x4';
   if (userCurrentlyProcessing[admin.id]) classList += ' disabled loading';
   return classList;
 };

 const permissionsLabel = (admin) => {
   const labelParts = [];
   if (admin.permission_label) {
     labelParts.push(admin.permission_label);
   }
   if (admin.id === state.currentUser.id) {
     labelParts.push('You!');
   }
   return labelParts.join(', ');
 };

 // Network Request
 const updateShoutoutsLevel = (userId, level) => {
   userCurrentlyProcessing[userId] = true;
   return new Promise((resolve, reject) => {
     Network.put(`/user_permissions/${userId}`, { user_permission: { shoutouts_level: level } }, {
       success: () => { resolve(); }, // Note that the actual response from this endpoint isn't required anywhere, only the fact that it succeeded
       error: () => { reject(); },
       complete: () => { userCurrentlyProcessing[userId] = false; },
     });
   });
 };

 // Admin Updates
 const addAdmin = (user) => {
   updateShoutoutsLevel(user.id, 'admin')
     .then(() => {
       admins.push(user);
       dispatch('setSuccessToast', `${user.name} is now a ${$i18n.t('shoutouts.feature')} admin!`);
     })
     .catch(() => dispatch('setErrorToast', `Could not add ${user.name}`))
     .finally(() => { userInSelector.value = null; });
 };
 const removeAdmin = (user) => {
   updateShoutoutsLevel(user.id, 'none')
     .then(() => {
       const adminIdx = admins.findIndex(a => a.id === user.id);
       admins.splice(adminIdx, 1);
       dispatch('setSuccessToast', `${user.name} is no longer a ${$i18n.t('shoutouts.feature')} admin.`);
     })
     .catch(() => dispatch('setErrorToast', `Could not remove ${user.name}`));
 };

 // User Selector
 watch(userInSelector, (user) => {
   if (user) {
     if (admins.some(u => u.id === user.id)) {
       nextTick(() => { userInSelector.value = null; });
     } else {
       addAdmin(user);
     }
   }
 });
</script>
